import { getUsage, type UsageResponse } from './api';

const STORAGE_KEY = 'ie_device_id_v1';
const USAGE_CACHE_KEY = 'ie_usage_cache_v1';
const USAGE_CACHE_TTL_MS = 60_000; // 1 minute

type CachedUsage = UsageResponse & {
  cachedAt: number;
};

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id = generateId();
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
}

function readCachedUsage(): UsageResponse {
  if (typeof window === 'undefined') return { dailyFreeUsed: 0, dailyFreeLimit: 2 };
  try {
    const raw = localStorage.getItem(USAGE_CACHE_KEY);
    if (!raw) return { dailyFreeUsed: 0, dailyFreeLimit: 2 };
    const parsed = JSON.parse(raw) as CachedUsage;
    if (Date.now() - parsed.cachedAt > USAGE_CACHE_TTL_MS) return { dailyFreeUsed: 0, dailyFreeLimit: 2 };
    return { dailyFreeUsed: parsed.dailyFreeUsed, dailyFreeLimit: parsed.dailyFreeLimit };
  } catch {
    return { dailyFreeUsed: 0, dailyFreeLimit: 2 };
  }
}

function saveCachedUsage(usage: UsageResponse) {
  if (typeof window === 'undefined') return;
  const cached: CachedUsage = { ...usage, cachedAt: Date.now() };
  localStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(cached));
}

export function getUsageStats(): { used: number; limit: number } {
  const cached = readCachedUsage();
  return { used: cached.dailyFreeUsed, limit: cached.dailyFreeLimit };
}

export function canProcess(): boolean {
  const { used, limit } = getUsageStats();
  return used < limit;
}

export async function refreshUsage(): Promise<UsageResponse> {
  const usage = await getUsage();
  saveCachedUsage(usage);
  return usage;
}

export function recordUsage(): boolean {
  if (!canProcess()) return false;
  const cached = readCachedUsage();
  cached.dailyFreeUsed += 1;
  saveCachedUsage(cached);
  return true;
}

export function resetUsageCache() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(USAGE_CACHE_KEY);
}

export { getToday };
